import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Info } from 'lucide-react';
import { cn } from '../cn';
const BUBBLE_WIDTH = 256; // w-64
const EDGE_MARGIN = 8;
const ICON_GAP = 8;
/**
 * Hover/focus info tooltip (ⓘ) used on every dashboard metric.
 *
 * The bubble is rendered with `position: fixed` and coordinates measured from
 * the icon, NOT with `absolute bottom-full`: several call sites (stats tables)
 * live inside `overflow-x-auto` wrappers, which clip absolutely-positioned
 * bubbles to a sliver over the header row. Fixed positioning escapes any
 * scroll container; the bubble clamps to the viewport and flips below the
 * icon when there is no room above.
 */
export const InfoTooltip = ({ content, title, className, align = 'center' }) => {
    const iconRef = useRef(null);
    const [pos, setPos] = useState(null);
    const show = useCallback(() => {
        var _a;
        const rect = (_a = iconRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
        if (!rect)
            return;
        let left = align === 'left'
            ? rect.left
            : align === 'right'
                ? rect.right - BUBBLE_WIDTH
                : rect.left + rect.width / 2 - BUBBLE_WIDTH / 2;
        left = Math.max(EDGE_MARGIN, Math.min(left, window.innerWidth - BUBBLE_WIDTH - EDGE_MARGIN));
        // Flip below when the bubble would poke above the viewport.
        const below = rect.top < 120;
        setPos({ top: below ? rect.bottom + ICON_GAP : rect.top - ICON_GAP, left, below });
    }, [align]);
    const hide = useCallback(() => setPos(null), []);
    // A fixed bubble does not follow its anchor — dismiss on scroll/resize.
    useEffect(() => {
        if (!pos)
            return undefined;
        window.addEventListener('scroll', hide, true);
        window.addEventListener('resize', hide);
        return () => {
            window.removeEventListener('scroll', hide, true);
            window.removeEventListener('resize', hide);
        };
    }, [pos, hide]);
    if (!content)
        return null;
    return (React.createElement("span", { className: cn('relative inline-flex', className), onMouseEnter: show, onMouseLeave: hide },
        React.createElement("button", { ref: iconRef, type: "button", tabIndex: 0, "aria-label": title ? `About ${title}` : 'About this metric', className: "inline-flex cursor-help items-center text-gray-400 transition hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400", onClick: (e) => e.preventDefault(), onFocus: show, onBlur: hide },
            React.createElement(Info, { size: 14, "aria-hidden": "true" })),
        pos ? (React.createElement("span", { role: "tooltip", style: {
                position: 'fixed',
                top: pos.top,
                left: pos.left,
                width: BUBBLE_WIDTH,
                transform: pos.below ? undefined : 'translateY(-100%)',
            }, className: "pointer-events-none z-50 rounded-lg bg-gray-900 px-3 py-2 text-left shadow-lg" },
            title ? React.createElement("span", { className: "mb-0.5 block text-xs font-semibold text-white" }, title) : null,
            React.createElement("span", { className: "block text-xs leading-relaxed text-gray-200" }, content))) : null));
};
export default InfoTooltip;
